import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CreditCard, ExternalLink, Loader2, AlertTriangle, CheckCircle2, Shield, Users } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useBillingData, useBillingMutations, billingKeys, type Subscription, type DataIntegrityError } from "@/hooks/billing";
import { useEntitlementContext } from "@/providers/EntitlementProvider";
import { supabase } from "@/integrations/supabase/client";

/**
 * Hook to fetch the current billable seat count for the company.
 * Uses the get_billable_employee_count RPC.
 */
function useBillableSeatCount(companyId: string | undefined) {
  return useQuery({
    queryKey: ['billable-seat-count', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_billable_employee_count', { p_company_id: companyId! });
      if (error) throw error;
      return (data as number) ?? 0;
    },
    enabled: !!companyId,
    staleTime: 60_000,
  });
}

export default function BillingSettings() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [postCheckout, setPostCheckout] = useState(false);
  const { source, planName, isTrial, trialEnd, trialDaysRemaining, isActive: isEntitled } = useEntitlementContext();
  
  // React Query hooks
  const { 
    data: billingData, 
    isLoading: loading,
    error: billingError 
  } = useBillingData(profile?.company_id);

  const { data: billableSeatCount, isLoading: seatsLoading } = useBillableSeatCount(
    source === 'stripe' ? profile?.company_id : undefined
  );

  // Detect ?success=true from Stripe return — invalidate billing cache and poll
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') !== 'true') return;

    setPostCheckout(true);

    // Immediately invalidate stale billing data
    queryClient.invalidateQueries({ queryKey: billingKeys.all });

    // Poll every 2s for up to 30s until subscription appears
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: billingKeys.all });
      queryClient.invalidateQueries({ queryKey: ['entitlement'] });
    }, 2000);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      setPostCheckout(false);
    }, 30_000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [location.search, queryClient]);

  // Stop polling once subscription data arrives
  useEffect(() => {
    if (postCheckout && billingData?.subscription) {
      setPostCheckout(false);
      // Clean up ?success=true from URL
      navigate('/business-settings/billing', { replace: true });
    }
  }, [postCheckout, billingData?.subscription, navigate]);
  
  const { createPortalSession } = useBillingMutations();
  
  // Destructure billing data
  const subscription = billingData?.subscription || null;
  const dataIntegrityError = billingData?.dataIntegrityError || null;

  const handleManageBilling = () => {
    createPortalSession.mutate();
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'default';
      case 'canceled':
      case 'incomplete_expired':
        return 'destructive';
      case 'trialing':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Billing & Subscription</h1>
        <p className="text-muted-foreground">
          Manage your subscription and billing information
        </p>
      </div>

      {/* Post-checkout processing banner */}
      {postCheckout && !billingData?.subscription && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="flex items-center gap-3 py-4">
            <Loader2 className="h-5 w-5 animate-spin text-primary flex-shrink-0" />
            <div>
              <p className="font-medium text-sm">Setting up your subscription…</p>
              <p className="text-xs text-muted-foreground">This usually takes a few seconds.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Integrity Error Display */}
      {dataIntegrityError && (
        <Card className="border-destructive bg-destructive/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Data Integrity Error
            </CardTitle>
            <CardDescription>
              A critical data issue has been detected with your subscription records.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm">
                <strong>Error:</strong> Multiple active subscriptions were found for your account.
              </p>
              <p className="text-sm text-muted-foreground">
                Found {dataIntegrityError.count} subscription records for company ID: {dataIntegrityError.companyId}
              </p>
            </div>
            
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">What this means:</p>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Your account has duplicate subscription records in our database</li>
                <li>This prevents us from displaying your billing information correctly</li>
                <li>This issue needs to be resolved by our support team</li>
              </ul>
            </div>

            <div className="pt-4 border-t">
              <p className="text-sm font-medium mb-2">Next steps:</p>
              <p className="text-sm text-muted-foreground mb-4">
                Please contact our support team with the error details above. 
                They will resolve this data integrity issue for you.
              </p>
              <Button variant="outline" className="w-full sm:w-auto">
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Normal Subscription Display */}
      {!dataIntegrityError && subscription && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Current Subscription
            </CardTitle>
            <CardDescription>
              {subscription.status === 'trialing'
                ? 'You are currently on a free trial'
                : 'Your active subscription details and billing information'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">{subscription.plan_name}</h3>
                {subscription.status === 'trialing' && subscription.trial_end ? (
                  <p className="text-sm text-muted-foreground">
                    Trial ends: {formatDate(subscription.trial_end)}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Billing period: {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
                  </p>
                )}
              </div>
              <Badge variant={getStatusBadgeVariant(subscription.status)}>
                {subscription.status === 'trialing' ? 'Trial' : subscription.status}
              </Badge>
            </div>

            {/* Billable Seat Count — only shown for Stripe-source subscriptions */}
            {source === 'stripe' && (
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <h4 className="font-medium text-sm">Employee Billing</h4>
                </div>
                {seatsLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span className="text-sm text-muted-foreground">Loading...</span>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="text-sm">
                      <span className="font-medium">{(billableSeatCount ?? 0) + 1}</span> active employee{((billableSeatCount ?? 0) + 1) !== 1 ? 's' : ''}
                      {' · '}
                      <span className="font-medium">{billableSeatCount ?? 0}</span> billable seat{(billableSeatCount ?? 0) !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Base plan includes 1 employee. Additional employees are billed at €1/month each.
                    </p>
                    {subscription.status !== 'trialing' && (billableSeatCount ?? 0) > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Estimated seat charge: €{billableSeatCount}/month
                      </p>
                    )}
                    {subscription.status === 'trialing' && (
                      <p className="text-xs text-muted-foreground">
                        Seat billing starts when your trial ends.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="pt-4 border-t">
              <Button 
                onClick={handleManageBilling}
                disabled={createPortalSession.isPending}
                className="w-full sm:w-auto"
              >
                {createPortalSession.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Manage Billing
                  </>
                )}
              </Button>
              <p className="text-sm text-muted-foreground mt-2">
                Update payment methods, view invoices, and manage your subscription
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Override-only Access Display — no seat billing UI */}
      {!dataIntegrityError && !subscription && source === 'override' && isEntitled && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Access Granted
            </CardTitle>
            <CardDescription>
              Your access is provided via {planName || 'an internal grant'}.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">{planName || 'Internal Access'}</h3>
                {isTrial && trialEnd && (
                  <p className="text-sm text-muted-foreground">
                    {trialDaysRemaining != null && trialDaysRemaining > 0
                      ? `Expires in ${trialDaysRemaining} day${trialDaysRemaining !== 1 ? 's' : ''} (${formatDate(trialEnd)})`
                      : `Expires: ${formatDate(trialEnd)}`
                    }
                  </p>
                )}
                {!isTrial && (
                  <p className="text-sm text-muted-foreground">Unlimited duration</p>
                )}
              </div>
              <Badge variant="default">Active</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Subscription Display (only for non-override companies) */}
      {!dataIntegrityError && !subscription && !(source === 'override' && isEntitled) && (
        <Card>
          <CardHeader>
            <CardTitle>No Active Subscription</CardTitle>
            <CardDescription>
              You don't have an active subscription. Choose a plan to get started.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/pricing")}>
              View Pricing Plans
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
